import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { useCountries, useStateProvinces } from '../lookups/useLookups';
import { saveShipTo, type CustomerShipToDto } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  customerId: string;
  /** Null to add a new address. */
  shipTo: CustomerShipToDto | null;
  /** True when this is the customer's first address, which the server will make the default. */
  isFirst: boolean;
  onSaved: () => void;
}

interface FormState {
  shipToCode: string;
  shipToName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  countryCode: string;
  phone: string;
  notes: string;
  isDefault: boolean;
  isActive: boolean;
}

/**
 * The country a new address starts on.
 *
 * A default rather than an empty picker because the field is required and all-but-one tenant's
 * addresses are in one country — making everybody choose it every time is a tax on the common case.
 * It is only a starting value; the picker is right there.
 */
const DEFAULT_COUNTRY = 'US';

function emptyForm(): FormState {
  return {
    shipToCode: '',
    shipToName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    countryCode: DEFAULT_COUNTRY,
    phone: '',
    notes: '',
    isDefault: false,
    isActive: true,
  };
}

/**
 * One delivery address.
 *
 * A separate modal from the customer rather than a grid inside it, because an address has ten
 * fields of its own and a grid of ten-field rows is unusable on a phone — and the people who
 * maintain customers do it from a tablet on the floor as often as from a desk.
 *
 * The country and state are pickers, not text boxes: both columns are foreign keys, so a typed code
 * would reach the database and come back as an unexplained 500 rather than as "that country does
 * not exist".
 */
export function ShipToFormModal({ opened, onClose, customerId, shipTo, isFirst, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = shipTo !== null;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const countries = useCountries();
  const states = useStateProvinces(form.countryCode || null);

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setForm(
      shipTo
        ? {
            shipToCode: shipTo.shipToCode,
            shipToName: shipTo.shipToName,
            addressLine1: shipTo.addressLine1,
            addressLine2: shipTo.addressLine2 ?? '',
            city: shipTo.city,
            stateProvince: shipTo.stateProvince ?? '',
            postalCode: shipTo.postalCode ?? '',
            countryCode: shipTo.countryCode,
            phone: shipTo.phone ?? '',
            notes: shipTo.notes ?? '',
            isDefault: shipTo.isDefault,
            isActive: shipTo.isActive,
          }
        : emptyForm(),
    );
  }, [opened, shipTo]);

  const countryOptions = useMemo(
    () =>
      (countries.data ?? []).map((c) => ({ value: c.countryCode, label: c.countryName })),
    [countries.data],
  );

  const stateOptions = useMemo(
    () =>
      (states.data ?? []).map((s) => ({
        value: s.stateProvinceCode,
        label: `${s.stateProvinceCode} — ${s.stateProvinceName}`,
      })),
    [states.data],
  );

  const save = useMutation({
    mutationFn: () =>
      saveShipTo(customerId, {
        ...(isEdit ? { customerShipToId: shipTo!.customerShipToId } : {}),
        shipToCode: form.shipToCode.trim(),
        shipToName: form.shipToName.trim(),
        addressLine1: form.addressLine1.trim(),
        city: form.city.trim(),
        countryCode: form.countryCode,
        addressLine2: blankToNull(form.addressLine2),
        stateProvince: form.stateProvince || null,
        postalCode: blankToNull(form.postalCode),
        phone: blankToNull(form.phone),
        notes: blankToNull(form.notes),
        isDefault: form.isDefault,
        isActive: form.isActive,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.problem.type?.includes('duplicate_shipto_code')) {
        return t('customers.shipTos.errors.duplicateCode', { code: form.shipToCode.trim() });
      }
      // shipto_incomplete names the field in its detail, which is more use than anything composed
      // here — the server knows which one it rejected.
      return err.problem.detail ?? err.problem.title ?? t('customers.shipTos.errors.saveFailed');
    }
    return t('customers.shipTos.errors.saveFailed');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    // Checked here as well as on the server so the field lights up instead of a round trip
    // answering with a sentence. The server check is the one that counts.
    const next: typeof errors = {};
    if (!form.shipToCode.trim()) next.shipToCode = t('customers.shipTos.errors.codeRequired');
    if (!form.shipToName.trim()) next.shipToName = t('customers.shipTos.errors.nameRequired');
    if (!form.addressLine1.trim()) next.addressLine1 = t('customers.shipTos.errors.addressRequired');
    if (!form.city.trim()) next.city = t('customers.shipTos.errors.cityRequired');
    if (!form.countryCode) next.countryCode = t('customers.shipTos.errors.countryRequired');

    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate();
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={
        isEdit
          ? t('customers.shipTos.editTitle', { name: shipTo!.shipToName })
          : t('customers.shipTos.newTitle')
      }
      width="lg"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('customers.shipTos.code')}
              description={t('customers.shipTos.codeHint')}
              value={form.shipToCode}
              onChange={(e) => setForm({ ...form, shipToCode: e.currentTarget.value })}
              error={errors.shipToCode}
              maxLength={50}
              required
              autoFocus
            />
            <TextInput
              label={t('customers.shipTos.name')}
              value={form.shipToName}
              onChange={(e) => setForm({ ...form, shipToName: e.currentTarget.value })}
              error={errors.shipToName}
              maxLength={200}
              required
            />
          </SimpleGrid>

          <TextInput
            label={t('customers.shipTos.address1')}
            value={form.addressLine1}
            onChange={(e) => setForm({ ...form, addressLine1: e.currentTarget.value })}
            error={errors.addressLine1}
            maxLength={255}
            required
          />

          <TextInput
            label={t('customers.shipTos.address2')}
            value={form.addressLine2}
            onChange={(e) => setForm({ ...form, addressLine2: e.currentTarget.value })}
            maxLength={255}
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('customers.shipTos.city')}
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.currentTarget.value })}
              error={errors.city}
              maxLength={100}
              required
            />
            <Select
              label={t('customers.shipTos.country')}
              data={countryOptions}
              value={form.countryCode || null}
              // Changing the country clears the state: the codes are per country, so keeping the
              // old one would submit a subdivision that does not belong to the country beside it.
              onChange={(v) => setForm({ ...form, countryCode: v ?? '', stateProvince: '' })}
              error={errors.countryCode}
              disabled={countries.isLoading}
              searchable
              required
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label={t('customers.shipTos.stateProvince')}
              description={
                stateOptions.length === 0 && !states.isLoading
                  ? t('customers.shipTos.noStates')
                  : undefined
              }
              placeholder={t('customers.shipTos.statePlaceholder')}
              data={stateOptions}
              value={form.stateProvince || null}
              onChange={(v) => setForm({ ...form, stateProvince: v ?? '' })}
              disabled={states.isLoading || stateOptions.length === 0}
              searchable
              clearable
            />
            <TextInput
              label={t('customers.shipTos.postalCode')}
              value={form.postalCode}
              onChange={(e) => setForm({ ...form, postalCode: e.currentTarget.value })}
              maxLength={20}
            />
          </SimpleGrid>

          <TextInput
            label={t('customers.shipTos.phone')}
            description={t('customers.shipTos.phoneHint')}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
            maxLength={50}
          />

          <Textarea
            label={t('customers.shipTos.notes')}
            description={t('customers.shipTos.notesHint')}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.currentTarget.value })}
            maxLength={255}
            autosize
            minRows={2}
            maxRows={4}
          />

          <Switch
            label={t('customers.shipTos.isDefault')}
            description={
              isFirst && !isEdit
                ? t('customers.shipTos.isDefaultFirst')
                : t('customers.shipTos.isDefaultHint')
            }
            checked={form.isDefault || (isFirst && !isEdit)}
            // The first address is the default whatever the box says, so it is shown ticked and
            // locked rather than offering a choice the server will overrule.
            disabled={isFirst && !isEdit}
            onChange={(e) => setForm({ ...form, isDefault: e.currentTarget.checked })}
          />

          <Switch
            label={t('customers.shipTos.active')}
            description={t('customers.shipTos.activeHint')}
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
          />

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          {countries.isError && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">{t('customers.shipTos.errors.countriesFailed')}</Text>
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={save.isPending}>
              {t('shell.saveChanges')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}

/** An all-whitespace field becomes null rather than a blank that reads as data. */
function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
